import './file-upload.scss';

import { notification } from 'antd';
import { useEffect, useRef } from 'react';

import { downloadFilesApi } from '@/integrations/index.api';
import FileUploadIcon from '@/media/icons/file-upload.svg?react';
import TrashIcon from '@/media/icons/trash.svg?react';
import { handleError } from '@/services/handleError';
import { convertFileToBase64, formatFileSize } from '@/services/helpers/files';
import { sanitizeFileName } from '@/services/helpers/strings';
import type { MakeOptional } from '@/types/app';
import type { FileType } from '@/types/uploadFiles';

interface IProps {
  title: string;
  file?: MakeOptional<FileType, 'size' | 'base64'>;
  placeholder?: string;
  helperText?: string;
  error?: string;
  onChange: (file: Partial<FileType>) => void;
  onDelete?: () => void;
  isFrozen?: boolean;
  isRequired?: boolean;
  accept?: string;
  allowedExtensions?: string[];
  initialFile?: FileType;
}

const FileUpload: React.FC<IProps> = ({
  title,
  file,
  placeholder = 'Выбрать файл',
  helperText,
  error,
  onChange,
  onDelete,
  isFrozen,
  isRequired,
  accept,
  allowedExtensions,
  initialFile
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const computedFile = file || initialFile;

  useEffect(() => {
    // Подгрузка файла и установка его в нужном формате в состояние формы
    const fetchBase64 = async () => {
      if (!(file && 'uuid' in file && file?.uuid)) return;

      try {
        const fileUuid = 'uuid' in file ? (file.uuid as string) : '';
        const response = await downloadFilesApi.downloadBankFile(
          fileUuid || ''
        );

        const base64Response = await fetch(
          `data:image/webp;base64,${response.base64}`
        );
        const blob = await base64Response.blob();
        const imageFile = new File([blob], response.name);

        onChange({
          base64: response.base64,
          name: imageFile.name,
          size: imageFile.size
        });
      } catch (error) {
        handleError(error);
      }
    };

    if (file && 'uuid' in file && file?.uuid) {
      fetchBase64();
    }
  }, [file]);

  const handleFileChanged = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newFile = event.target.files?.[0];
    if (!newFile) {
      return;
    }

    const fileExtension = newFile.name.split('.').pop()?.toLowerCase();
    if (allowedExtensions && !allowedExtensions.includes(fileExtension || '')) {
      const extensionsText = allowedExtensions
        .map((item) => `.${item}`)
        .join(', ');
      notification.error({
        title: 'Недопустимое расширение файла!',
        description: `Пожалуйста, загрузите файл в формате ${extensionsText}`
      });
      return;
    }

    try {
      const file = await convertFileToBase64(newFile);
      const base64 = file[1];
      const format = file[0];
      onChange({ name: newFile.name, base64, size: newFile.size, format });
    } catch (error) {
      handleError(error, {
        onError: () => {
          onChange({ name: undefined, base64: undefined, size: undefined });
        }
      });
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const isShowDelete = !isFrozen && file;

  return (
    <div className="file-upload">
      <label>
        {!isFrozen && (
          <input
            ref={inputRef}
            className="file-upload__field"
            type="file"
            onChange={handleFileChanged}
            accept={accept}
          />
        )}

        <div
          className={`file-upload__view ${
            error ? 'file-upload__view--error' : ''
          }`}
        >
          <FileUploadIcon />

          <div className="file-upload__file-info">
            <p className="file-upload__title">
              {title}
              {isRequired && <span className="file-upload__require">*</span>}
            </p>
            <p
              className={`file-upload__value ${
                !computedFile ? 'file-upload__value--no-file' : ''
              }`}
            >
              {(initialFile && !file
                ? `${sanitizeFileName(title)}.${initialFile.name.split('.').pop()}`
                : file?.name) || placeholder}
              {typeof computedFile?.size === 'number' &&
                computedFile.size > 0 && (
                  <span className="file-upload__chosen-size">
                    {formatFileSize(computedFile.size)}
                  </span>
                )}
            </p>
          </div>

          {isShowDelete && onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (inputRef.current) {
                  inputRef.current.value = '';
                }
                onDelete();
              }}
              className="file-upload__delete"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </label>

      {error ? (
        <p className="file-upload__additional-text file-upload__additional-text--error">
          {error}
        </p>
      ) : (
        helperText && (
          <p className="file-upload__additional-text">{helperText}</p>
        )
      )}
    </div>
  );
};

export default FileUpload;
